import { memo, useCallback, useState } from 'react';
import { isEmpty } from 'lodash';
import {
  IconButton, Drawer as MuiDrawer, Avatar, Popover,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import MenuIcon from '@mui/icons-material/Menu';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useSelector } from '@client/hooks/redux.hooks';
import { userIsImpersonating } from '@client/selectors/helpers/login';
import { getSelectedProfileAvatar } from '@client/selectors/helpers/profile';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Links } from '../common/Links';
import { Header } from './Header';

const useStyles = makeStyles()((theme) => ({
  iconButton: {
    marginLeft: theme.spacing(1),

    [theme.breakpoints.up('md')]: {
      margin: '0 auto !important',
    },
  },
  drawerRoot: {
    zIndex: theme.zIndex.drawer + 2,

    [theme.breakpoints.up('md')]: {
      zIndex: theme.zIndex.drawer - 1,
    },
  },
  drawerPaper: {
    width: 360,
    overflow: 'auto',
    backgroundColor: theme.palette.grey[200],

    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
      width: '100vw',
      overflow: 'visible',
    },

    [theme.breakpoints.up('md')]: {
      left: 86,
    },
  },
}));

export const Drawer = memo(() => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);

  const handleOpen = useCallback((e) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  const isImpersonating = useSelector(userIsImpersonating);
  const publicImageUrl = useSelector(getSelectedProfileAvatar);

  return (
    <>
      <IconButton
        id="sda-main-menu-button"
        size="large"
        onClick={handleOpen}
        className={classes.iconButton}
        color={isImpersonating ? 'primary' : 'inherit'}
        edge="end"
        aria-label="Open main menu"
        aria-controls={open ? 'sda-main-menu-drawer' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {isImpersonating ? <SupportAgentIcon /> : (
          <>
            {!isEmpty(publicImageUrl) ? <Avatar src={publicImageUrl} /> : <MenuIcon />}
          </>
        )}
      </IconButton>

      {isMobile ? (
        <MuiDrawer
          id="sda-main-menu-drawer"
          aria-labelledby="sda-main-menu-button"
          anchor="right"
          onClose={handleClose}
          open={open}
          variant="temporary"
          elevation={4}
          classes={{
            root: classes.drawerRoot,
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <Header
            isImpersonating={isImpersonating}
            handleClose={handleClose}
          />

          <Links handleClose={handleClose} />
        </MuiDrawer>
      ) : (
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          {isImpersonating && (
            <Header
              isImpersonating={isImpersonating}
              handleClose={handleClose}
            />
          )}

          <Links handleClose={handleClose} />
        </Popover>
      )}
    </>
  );
});
